import apiService from './apiService';

// TypeScript interfaces
export interface InvoiceItemTemplate {
  id: string;
  companyId: string;
  itemCode: string;
  description: string;
  category?: string;
  unitPrice: number;
  accountId?: string; // Default revenue account for this template
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  account?: {
    id: string;
    code: string;
    name: string;
    accountType: string;
  };
}

export interface CreateInvoiceItemTemplateInput {
  itemCode: string;
  description: string;
  category?: string;
  unitPrice: number;
  accountId?: string; // Default revenue account for this template
}

export interface UpdateInvoiceItemTemplateInput {
  itemCode?: string;
  description?: string;
  category?: string;
  unitPrice?: number;
  accountId?: string; // Default revenue account for this template
  isActive?: boolean;
}

export interface InvoiceItemTemplateFilters {
  category?: string;
  isActive?: boolean;
  searchTerm?: string;
}

export interface InvoiceItemTemplateStats {
  totalTemplates: number;
  activeTemplates: number;
  inactiveTemplates: number;
  categoriesCount: number;
}

class InvoiceItemTemplateService {
  private baseUrl = '/accounting/invoice-item-templates';

  /**
   * Get all invoice item templates with optional filters
   */
  async getAllTemplates(filters?: InvoiceItemTemplateFilters): Promise<InvoiceItemTemplate[]> {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
    if (filters?.searchTerm) params.append('searchTerm', filters.searchTerm);

    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    const response = await apiService.get(url);
    return response.data;
  }

  /**
   * Get active invoice item templates only
   */
  async getActiveTemplates(): Promise<InvoiceItemTemplate[]> {
    const response = await apiService.get(`${this.baseUrl}/active`);
    return response.data;
  }

  /**
   * Get invoice item template by ID
   */
  async getTemplateById(id: string): Promise<InvoiceItemTemplate> {
    const response = await apiService.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Create a new invoice item template
   */
  async createTemplate(data: CreateInvoiceItemTemplateInput): Promise<InvoiceItemTemplate> {
    const response = await apiService.post(this.baseUrl, data);
    return response.data;
  }

  /**
   * Update an existing invoice item template
   */
  async updateTemplate(id: string, data: UpdateInvoiceItemTemplateInput): Promise<InvoiceItemTemplate> {
    const response = await apiService.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Delete an invoice item template
   */
  async deleteTemplate(id: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Deactivate an invoice item template
   */
  async deactivateTemplate(id: string): Promise<InvoiceItemTemplate> {
    const response = await apiService.post(`${this.baseUrl}/${id}/deactivate`, {});
    return response.data;
  }

  /**
   * Activate an invoice item template
   */
  async activateTemplate(id: string): Promise<InvoiceItemTemplate> {
    const response = await apiService.post(`${this.baseUrl}/${id}/activate`, {});
    return response.data;
  }

  /**
   * Get all categories
   */
  async getCategories(): Promise<string[]> {
    const response = await apiService.get(`${this.baseUrl}/categories`);
    return response.data;
  }

  /**
   * Get invoice item template statistics
   */
  async getStats(): Promise<InvoiceItemTemplateStats> {
    const response = await apiService.get(`${this.baseUrl}/stats`);
    return response.data;
  }
}

export default new InvoiceItemTemplateService();
